import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaExternalLinkAlt,
  FaImage,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import {
  buildPartenairePayload,
  emptyPartenaireForm,
  mapPartenaireToForm,
  resolveMediaUrl,
  safeExternalUrl,
} from "../utils/partenairesManagerUtils";

export default function PartenaireFormDrawer({
  isOpen,
  mode = "create",
  partenaire,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptyPartenaireForm);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && partenaire) {
      setFormData(mapPartenaireToForm(partenaire));
    } else {
      setFormData(emptyPartenaireForm);
    }
  }, [isOpen, isEdit, partenaire]);

  const title = isEdit ? "Modifier un partenaire" : "Ajouter un partenaire";

  const subtitle = isEdit
    ? "Mettez à jour le logo, le lien, l’ordre et la visibilité."
    : "Créez un nouveau partenaire à afficher sur le site public.";

  const preview = resolveMediaUrl(
    formData.photo_url,
    "/images/asso_placeholder.webp"
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.nom) {
      toast.error("Le nom du partenaire est obligatoire.");
      return;
    }

    const payload = buildPartenairePayload(formData);

    if (isEdit) {
      await onUpdate?.(partenaire.id, payload);
    } else {
      await onCreate?.(payload);
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <FaImage />
              </span>

              <div>
                <h3 className="font-bold text-slate-950 dark:text-white">
                  Identité du partenaire
                </h3>

                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Renseignez le nom, le logo et le lien externe.
                </p>
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <InputField
                    label="Nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <InputField
                    label="URL du logo"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={handleChange}
                    placeholder="https://... ou /uploads/..."
                  />
                </div>

                <div className="sm:col-span-2">
                  <InputField
                    label="Lien externe"
                    name="link_url"
                    value={formData.link_url}
                    onChange={handleChange}
                    placeholder="https://www.partenaire.fr"
                  />

                  {formData.link_url && (
                    <a
                      href={safeExternalUrl(formData.link_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-2 text-xs font-bold text-primary"
                    >
                      Tester le lien
                      <FaExternalLinkAlt size={10} />
                    </a>
                  )}
                </div>

                <InputField
                  label="Ordre d'affichage"
                  name="ordre"
                  type="number"
                  value={formData.ordre}
                  onChange={handleChange}
                />

                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-950">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <FaEye />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-slate-900 dark:text-white">
                      Partenaire actif
                    </span>

                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                      Affiché sur le site public.
                    </span>
                  </span>

                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="h-5 w-5 rounded border-slate-300 text-primary focus:ring-primary"
                  />
                </label>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
                <p className="mb-3 text-sm font-bold text-slate-950 dark:text-white">
                  Aperçu du logo
                </p>

                <div className="flex aspect-square items-center justify-center overflow-hidden rounded-[24px] bg-slate-50 p-5 dark:bg-slate-900">
                  <img
                    src={preview}
                    alt="Aperçu partenaire"
                    className="max-h-full max-w-full object-contain"
                    onError={(event) => {
                      event.currentTarget.src = "/images/asso_placeholder.webp";
                    }}
                  />
                </div>

                <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Utilisez idéalement un logo avec fond transparent ou une image
                  bien cadrée.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}